// src/data/shop/shopData.js
// Shop item definitions with prices

// Awakening Store: cost in Veil Embers per piece
export const AWAKENING_EMBER_COST = 130;

/**
 * Shop item categories and their items
 * - id: matches item registry
 * - name: display name
 * - price: gold cost per item
 * - stock: -1 for unlimited, positive number for limited stock
 * - questUnlock: unlock ID required to purchase (checked at runtime)
 */
export const SHOP_ITEMS = {
    SKILLING: [
        { id: 'vial_of_water', name: 'Vial of Water', price: 10, stock: -1 },
        { id: 'spun_fiber', name: 'Spun Fiber', price: 15, stock: -1 },
        { id: 'feathers', name: 'Feathers', price: 8, stock: -1 },
        { id: 'feathers_daily', grantItemId: 'feathers', name: 'Feathers (Daily Free)', price: 0, stock: -1, dailyStock: 400, dailyId: 'feathers_daily' },
        // Starter arrows - free daily for ranged users
        { id: 'pyrestone_arrows_daily', grantItemId: 'pyrestone_arrows', name: 'Pyrestone Arrows (Daily Free)', price: 0, stock: -1, dailyStock: 400, dailyId: 'pyrestone_arrows_daily' },
        // Base elemental glyphs - free daily for magic users
        { id: 'flame_glyph_daily', grantItemId: 'flame_glyph', name: 'Flame Glyph (Daily Free)', price: 0, stock: -1, dailyStock: 150, dailyId: 'flame_glyph_daily' },
        { id: 'frost_glyph_daily', grantItemId: 'frost_glyph', name: 'Frost Glyph (Daily Free)', price: 0, stock: -1, dailyStock: 150, dailyId: 'frost_glyph_daily' },
        { id: 'stone_glyph_daily', grantItemId: 'stone_glyph', name: 'Stone Glyph (Daily Free)', price: 0, stock: -1, dailyStock: 150, dailyId: 'stone_glyph_daily' },
        { id: 'storm_glyph_daily', grantItemId: 'storm_glyph', name: 'Storm Glyph (Daily Free)', price: 0, stock: -1, dailyStock: 150, dailyId: 'storm_glyph_daily' },
        // Starter food - free daily for combat
        { id: 'cooked_sand_smelt_daily', grantItemId: 'cooked_sand_smelt', name: 'Cooked Sand Smelt (Daily Free)', price: 0, stock: -1, dailyStock: 50, dailyId: 'cooked_sand_smelt_daily' },
    ],
    // Starter combat equipment - weaker than crafted versions
    // Prices set to require ~2-3 minutes of pilfering for a full set
    COMBAT_WEAPONS: [
        { id: 'training_sword', name: 'Training Sword', price: 100, stock: -1 },
        { id: 'practice_bow', name: 'Practice Bow', price: 100, stock: -1, description: 'Includes worn arrows (no ammo needed)' },
        { id: 'apprentice_staff', name: 'Apprentice Staff', price: 100, stock: -1, description: 'Includes minor flame spell (no glyphs needed)' },
    ],
    // Starter armor sets - one for each combat style
    COMBAT_ARMOR_MELEE: [
        { id: 'trainee_helm', name: "Trainee's Helm", price: 60, stock: -1 },
        { id: 'trainee_chestplate', name: "Trainee's Chestplate", price: 100, stock: -1 },
        { id: 'trainee_legguards', name: "Trainee's Legguards", price: 80, stock: -1 },
    ],
    COMBAT_ARMOR_RANGED: [
        { id: 'scout_helm', name: "Scout's Cap", price: 60, stock: -1 },
        { id: 'scout_chestplate', name: "Scout's Vest", price: 100, stock: -1 },
        { id: 'scout_legguards', name: "Scout's Trousers", price: 80, stock: -1 },
    ],
    COMBAT_ARMOR_MAGIC: [
        { id: 'initiate_helm', name: "Initiate's Hood", price: 60, stock: -1 },
        { id: 'initiate_chestplate', name: "Initiate's Robe", price: 100, stock: -1 },
        { id: 'initiate_legguards', name: "Initiate's Leggings", price: 80, stock: -1 },
    ],
    // Quest reward items - can be repurchased after unlocking via quests
    // These only appear once the player has the corresponding unlock
    // Note: glyph_pouch is NOT here because it's a feature unlock, not an item
    QUEST_EQUIPMENT: [
        { id: 'leather_quiver', name: 'Leather Quiver', price: 500, stock: -1, questUnlock: 'quiver_leather', description: 'Ranged back slot. +12 Accuracy, +3% Speed' },
        { id: 'ironwood_quiver', name: 'Ironwood Quiver', price: 2000, stock: -1, questUnlock: 'quiver_ironwood', description: 'Ranged back slot. +22 Accuracy, +5% Speed' },
        { id: 'ashen_quiver', name: 'Ashen Quiver', price: 8000, stock: -1, questUnlock: 'quiver_ashen', description: 'Ranged back slot. +35 Accuracy, +8% Speed' },
        { id: 'veilstalker_quiver', name: 'Veilstalker Quiver', price: 25000, stock: -1, questUnlock: 'quiver_veilstalker', description: 'Ranged back slot. +50 Accuracy, +12% Speed' },
    ],
};

/**
 * Get all shop items as a flat array
 */
export function getAllShopItems() {
    const items = [];
    for (const category of Object.values(SHOP_ITEMS)) {
        items.push(...category);
    }
    return items;
}

/**
 * Find a shop item by ID
 */
export function findShopItem(itemId) {
    for (const category of Object.values(SHOP_ITEMS)) {
        const item = category.find(i => i.id === itemId);
        if (item) return item;
    }
    return null;
}

/**
 * Get shop items by category
 */
export function getShopItemsByCategory(categoryName) {
    return SHOP_ITEMS[categoryName] || [];
}

export default {
    SHOP_ITEMS,
    getAllShopItems,
    findShopItem,
    getShopItemsByCategory
};
